/**
 * schemas/common.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Shared building blocks for every response schema.
 *
 * WHY THE FRONT END VALIDATES AT ALL
 * The screens in this product render money, and a silently-missing field there
 * is worse than a loud failure: a balance that renders as "AED NaN" or, worse,
 * as "AED 0.00" is a number someone might act on. Parsing each response once,
 * at the service boundary, means a contract drift surfaces as one clear error
 * instead of as wrong figures scattered across four cards.
 *
 * Schemas here are deliberately NOT `.strict()` — the API may add fields, and
 * an additive change must never break a deployed client. Missing or wrongly
 * typed REQUIRED fields do fail, which is the point.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { z } from 'zod';

/**
 * Money on the wire: a minor-unit integer STRING. The API also emits numbers
 * for a few counters, so a number is coerced rather than rejected — but the
 * canonical form is the string, and lib/money.ts only ever reads it as one.
 */
export const minor = z.union([z.string(), z.number()]).transform((v) => String(v));

/** A nullable money field — `limit_minor` on a non-limit assignment, say. */
export const minorNullable = z
  .union([z.string(), z.number(), z.null()])
  .transform((v) => (v === null ? null : String(v)));

/**
 * A timestamp. `jsonSafe()` on the API turns every Date into an ISO string, so
 * that is what arrives; it is kept as a string here and formatted by
 * lib/dates.ts. Parsing it into a Date at the boundary would mean every memo
 * comparison saw a new object identity and re-ran.
 */
export const isoDate = z.string();
export const isoDateNullable = z.string().nullable();

/** ISO-4217-ish currency code. The org's own list decides which are offered. */
export const currency = z.string().min(2).max(4);

/** A per-currency total. Invariant 4: a total is a LIST, never one number. */
export const currencyTotal = z.object({
  cur: currency,
  total_minor: minor,
  count: z.number(),
});
export type CurrencyTotal = z.infer<typeof currencyTotal>;

/**
 * Parse a response, attributing any failure to the call that produced it.
 * Services use this instead of `schema.parse` so a contract break names the
 * endpoint in the message rather than pointing at an anonymous Zod path.
 */
export function parseResponse<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
  source: string,
): z.infer<T> {
  const result = schema.safeParse(data);
  if (result.success) return result.data;
  const issue = result.error.issues[0];
  const path = issue?.path.join('.') || '(root)';
  throw new Error(
    `${source} returned an unexpected shape at "${path}": ${issue?.message ?? 'invalid'}`,
  );
}

/** The `{ id, created }` envelope every idempotent write returns. */
export const writeResult = z.object({
  id: z.string().optional(),
  created: z.boolean().optional(),
  message: z.string().optional(),
});
export type WriteResult = z.infer<typeof writeResult>;
